/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, CardMedia, Typography, Grid, Checkbox, useTheme, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const CardList = ({
    data,
    selectedClusterName,
    selectedIds,
    setSelectedIds,
    selectedClusterId,
    setSelectedImageCluster,
    selectedImageCluster,
    setSelectedImageCount,
    deleteImage,
    setDeletedImage,
    selectedIdsURL,
    setSelectedIdsURL,
}) => {
    const theme = useTheme();
    const [softDeletedIds, setSoftDeletedIds] = useState([]);

    const handleToggle = (pointId, clusterId) => {
        const clusterStr = String(clusterId);
        const isSelected = selectedIds.includes(pointId);
        let updatedIds;
        let updatedImageClusters;
        let updatedUrls;

        const imageUrl = data.find((item) => item.pointId === pointId)?.linkToData;

        if (isSelected) {
            const indexToRemove = selectedIds.indexOf(pointId);
            updatedIds = [...selectedIds];
            updatedIds.splice(indexToRemove, 1);

            updatedImageClusters = [...selectedImageCluster];
            updatedImageClusters.splice(indexToRemove, 1);

            updatedUrls = [...selectedIdsURL];
            updatedUrls.splice(indexToRemove, 1);
        } else {
            updatedIds = [...selectedIds, pointId];
            updatedImageClusters = [...selectedImageCluster, clusterStr];
            updatedUrls = [...selectedIdsURL, imageUrl];
        }

        const clusterCount = {};
        updatedImageClusters.forEach((cId) => {
            if (cId) {
                clusterCount[cId] = (clusterCount[cId] || 0) + 1;
            }
        });

        setSelectedImageCount(clusterCount);
        setSelectedIds(updatedIds);
        setSelectedImageCluster(updatedImageClusters);
        setSelectedIdsURL(updatedUrls);
    };

    useEffect(() => {
        if (deleteImage) {
            const clusterStr = String(selectedClusterId);
            const idsToDelete = selectedIds.filter((_, index) => selectedImageCluster[index] === clusterStr);
            setSoftDeletedIds((prev) => [...new Set([...prev, ...idsToDelete])]);
            const updatedSelectedIds = selectedIds.filter((id) => !idsToDelete.includes(id));
            const updatedImageClusters = selectedImageCluster.filter((_, index) => {
                const idAtIndex = selectedIds[index];
                return !idsToDelete.includes(idAtIndex);
            });
            const updatedUrls = selectedIdsURL.filter((_, index) => {
                const idAtIndex = selectedIds[index];
                return !idsToDelete.includes(idAtIndex);
            });
            const newImageCount = {};
            updatedImageClusters.forEach((cId) => {
                newImageCount[cId] = (newImageCount[cId] || 0) + 1;
            });
            if (!updatedImageClusters.includes(clusterStr)) {
                newImageCount[clusterStr] = 0;
            }
            setSelectedIds(updatedSelectedIds);
            setSelectedImageCluster(updatedImageClusters);
            setSelectedImageCount(newImageCount);
            setSelectedIdsURL(updatedUrls);
            setDeletedImage(false);
        }
    }, [deleteImage]);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <Box sx={{ px: 2, py: 2 }}>
            <Grid container spacing={2}>
                {data
                    .filter((item) => !softDeletedIds.includes(item.pointId))
                    .map((item) => {
                        const isSelected = selectedIds.includes(item.pointId);
                        const imageUrl = item.linkToData;
                        const imageName = decodeURIComponent(imageUrl.split('/').pop().split('?')[0]);

                        return (
                            <Grid item xs={12} sm={6} md={3} lg={2} xl={1.5} key={item.pointId}>
                                <>
                                    <Card
                                        elevation={4}
                                        sx={{
                                            height: '100%',
                                            borderRadius: 3,
                                            backgroundColor: isSelected ? '#DEEAF8' : 'rgba(255,255,255,0.04)',
                                            boxShadow: isSelected ? `0 6px 20px rgba(0,0,0,0.3)` : `0 3px 12px rgba(0,0,0,0.1)`,
                                            transition: 'all 0.25s ease-in-out',
                                            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                            '&:hover': {
                                                transform: 'scale(1.03)',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
                                            <CardMedia
                                                component="img"
                                                image={item.linkToData}
                                                alt={`Image ${item.pointId}`}
                                                sx={{
                                                    height: 120,
                                                    width: '100%',
                                                    objectFit: 'cover',
                                                    transition: 'transform 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.1)',
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <CardContent sx={{ px: 1.5, py: 1 }}>
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => handleToggle(item.pointId, selectedClusterId)}
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            '&.Mui-checked': {
                                                                color: theme.palette.secondary.main,
                                                            },
                                                            p: 0.5,
                                                        }}
                                                    />
                                                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                                                        ID: {item.pointId}
                                                    </Typography>
                                                </Box>
                                                <Tooltip title="Copy URL">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopy(item.linkToData)}
                                                        sx={{
                                                            backgroundColor: '#4caf50',
                                                            color: '#fff',
                                                            borderRadius: 1,
                                                            p: 0.5,
                                                            ml: 1,
                                                            '&:hover': {
                                                                backgroundColor: '#388e3c',
                                                            },
                                                        }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" mt={0.5} noWrap>
                                                Label: {selectedClusterName ?? 'N/A'}
                                            </Typography>
                                            <Tooltip title={imageName}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                    sx={{
                                                        maxWidth: '100%',
                                                        display: 'block',
                                                    }}
                                                >
                                                    Name: {imageName}
                                                </Typography>
                                            </Tooltip>
                                        </CardContent>
                                    </Card>
                                </>
                            </Grid>
                        );
                    })}
            </Grid>
        </Box>
    );
};

CardList.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            pointId: PropTypes.string.isRequired,
            predictionScore: PropTypes.any,
            linkToData: PropTypes.string.isRequired,
            predictionLabel: PropTypes.string,
            selectedClusterName: PropTypes.string,
            selectedClusterId: PropTypes.string.isRequired,
            setSelectedImageCluster: PropTypes.func.isRequired,
            setSelectedImageCount: PropTypes.func.isRequired,
        })
    ).isRequired,
    selectedClusterName: PropTypes.string,
};

CardList.defaultProps = {
    selectedClusterName: '',
};

export default CardList;
